import { useTranslation } from 'react-i18next';

import { type ApiError, HTTP_STATUS, isSchemaValidationError } from '@brightlocal/api-client';
import { sonner } from '@brightlocal/ui-components/sonner';

import { useZodForm } from 'shared/forms';
import { useAppNavigation } from 'shared/hooks';
import { captureSchemaValidationError, captureSentryException } from 'shared/utils';

import { useAuthFormTracking } from '../../shared';
import {
  RESET_PASSWORD_ERROR_TYPE,
  RESET_PASSWORD_FEATURE_TAG,
  RESET_PASSWORD_FIELDS,
  RESET_PASSWORD_FORM_TRACKING_CONFIG,
  RESET_PASSWORD_SERVER_FIELDS,
  RESET_PASSWORD_VALIDATION_ERROR_CODES
} from '../consts';
import { getResetPasswordValidationErrorMessage, mapResetPasswordServerErrorsToFieldErrors } from '../utils';
import { useResetPasswordMutation } from './useResetPasswordMutation';
import { useResetPasswordSchema, type ResetPasswordFormValues } from './useResetPasswordSchema';

interface UseResetPasswordFormOptions {
  /** Email address the reset code was sent to. */
  email: string;
}

type ResetPasswordServerField = (typeof RESET_PASSWORD_SERVER_FIELDS)[keyof typeof RESET_PASSWORD_SERVER_FIELDS];

/** Form-registered field names plus 'root' for server fields with no UI counterpart (e.g. email). */
type MappableFormField = (typeof RESET_PASSWORD_FIELDS)[keyof typeof RESET_PASSWORD_FIELDS] | 'root';

/** Maps a server field name to the matching form field name used by RHF. */
const SERVER_TO_FORM_FIELD: Partial<Record<ResetPasswordServerField, MappableFormField>> = {
  [RESET_PASSWORD_SERVER_FIELDS.EMAIL]: 'root',
  [RESET_PASSWORD_SERVER_FIELDS.CODE]: RESET_PASSWORD_FIELDS.CODE,
  [RESET_PASSWORD_SERVER_FIELDS.NEW_PASSWORD]: RESET_PASSWORD_FIELDS.NEW_PASSWORD,
  [RESET_PASSWORD_SERVER_FIELDS.PASSWORD_CONFIRMATION]: RESET_PASSWORD_FIELDS.PASSWORD_CONFIRMATION
};

/** Hook managing the reset-password form: validation, submission, and post-success UX. */
export const useResetPasswordForm = ({ email }: UseResetPasswordFormOptions) => {
  const { t } = useTranslation();
  const schema = useResetPasswordSchema();
  const { mutateAsync, isPending } = useResetPasswordMutation();
  const { trackStarted, trackCompleted, trackError } = useAuthFormTracking(RESET_PASSWORD_FORM_TRACKING_CONFIG);
  const { toLogin } = useAppNavigation();

  const setRootError = (message: string) => {
    form.setError('root', { message });
    trackError(message, 'server');
  };

  const handleServerValidationError = (apiError: ApiError) => {
    const fieldErrors = mapResetPasswordServerErrorsToFieldErrors(apiError.error);

    if (!fieldErrors.length) {
      setRootError(t('auth.resetPassword.errors.serverError'));
      captureSentryException({
        message: 'Validation error with no field errors',
        feature: RESET_PASSWORD_FEATURE_TAG,
        error: apiError,
        extra: { method: 'handleServerValidationError' }
      });
      return;
    }

    const hasMaxAttemptsError = fieldErrors.some(
      ({ code }) => code === RESET_PASSWORD_VALIDATION_ERROR_CODES.MAX_ATTEMPTS_REACHED
    );

    if (hasMaxAttemptsError) {
      const message = t('auth.resetPassword.errors.maxAttemptsReached');

      form.setError('root', { type: RESET_PASSWORD_ERROR_TYPE.MAX_ATTEMPTS_REACHED, message });
      trackError(message, 'server');
      return;
    }

    fieldErrors.forEach(({ field, code }) => {
      const formField = SERVER_TO_FORM_FIELD[field];

      if (!formField) return;

      if (formField === 'root') {
        form.setError('root', { message: t('auth.resetPassword.errors.serverError') });
        return;
      }

      form.setError(formField, {
        message: getResetPasswordValidationErrorMessage(code, t, formField)
      });
    });

    const [first] = fieldErrors;
    const firstFormField = SERVER_TO_FORM_FIELD[first.field];
    const isRootMapped = !firstFormField || firstFormField === 'root';

    trackError(
      isRootMapped
        ? t('auth.resetPassword.errors.serverError')
        : getResetPasswordValidationErrorMessage(first.code, t, firstFormField),
      'server'
    );
  };

  const handleError = (apiError: ApiError) => {
    if (isSchemaValidationError(apiError)) {
      captureSchemaValidationError({
        feature: RESET_PASSWORD_FEATURE_TAG,
        method: 'apiClient.auth.resetPassword',
        error: apiError
      });

      setRootError(t('auth.resetPassword.errors.serverError'));
      return;
    }

    switch (apiError.status) {
      case HTTP_STATUS.VALIDATION_ERROR:
        handleServerValidationError(apiError);
        return;
      default:
        setRootError(t('auth.resetPassword.errors.serverError'));
        captureSentryException({
          message: 'Failed to submit reset-password form',
          feature: RESET_PASSWORD_FEATURE_TAG,
          error: apiError,
          extra: { method: 'handleError', status: apiError.status }
        });
    }
  };

  const { form, handleFormSubmit } = useZodForm({
    schema,
    defaultValues: {
      [RESET_PASSWORD_FIELDS.CODE]: '',
      [RESET_PASSWORD_FIELDS.NEW_PASSWORD]: '',
      [RESET_PASSWORD_FIELDS.PASSWORD_CONFIRMATION]: ''
    },
    validationMode: 'onTouched',
    onSubmit: async ({ code, newPassword, passwordConfirmation }: ResetPasswordFormValues) => {
      trackStarted();

      await mutateAsync({ email, code, newPassword, passwordConfirmation });

      trackCompleted();
      sonner({
        title: t('auth.resetPassword.success.title'),
        description: t('auth.resetPassword.success.description'),
        icon: 'success'
      });
      toLogin();
    },
    onError: (error) => {
      handleError(error as ApiError);
    }
  });

  const isLoading = form.formState.isSubmitting || isPending;
  const isMaxAttemptsReached = form.formState.errors.root?.type === RESET_PASSWORD_ERROR_TYPE.MAX_ATTEMPTS_REACHED;

  return {
    form,
    canSubmit: form.formState.isValid,
    handleFormSubmit,
    isLoading,
    isMaxAttemptsReached,
    rootErrorMessage: form.formState.errors.root?.message
  };
};
